// A* pathfinding on the city road grid

const STEP_DIRS: ReadonlyArray<readonly [number, number]> = [[0, 1], [0, -1], [1, 0], [-1, 0]];

interface GridNode {
  x: number;
  z: number;
  g: number;
  h: number;
  f: number;
  parent: GridNode | null;
}

export class CityGrid {
  private roads: Set<string>;
  private roadList: Array<[number, number]>;

  constructor(_gridW: number, _gridH: number, roadCells: Array<[number, number]>) {
    this.roads = new Set(roadCells.map(([x, z]) => `${x},${z}`));
    this.roadList = roadCells.slice();
  }

  /**
   * Infinitown-style ambient traffic path: a random walk along connected road
   * cells with no immediate backtracking. Cheap, endpoint-free, and produces
   * continuous wandering routes — unlike A* findPath which is reserved for
   * scenario "hero" vehicles routed between named infrastructure.
   */
  wanderPath(maxLen = 40): Array<[number, number]> | null {
    if (this.roadList.length === 0) return null;
    const start = this.roadList[Math.floor(Math.random() * this.roadList.length)];
    const path: Array<[number, number]> = [start];
    let [cx, cz] = start;
    let prev: [number, number] | null = null;

    for (let i = 0; i < maxLen; i++) {
      const options = STEP_DIRS
        .map(([dx, dz]) => [cx + dx, cz + dz] as [number, number])
        .filter(([nx, nz]) =>
          this.isRoad(nx, nz) && !(prev && nx === prev[0] && nz === prev[1]));
      if (options.length === 0) break;
      const next = options[Math.floor(Math.random() * options.length)];
      path.push(next);
      prev = [cx, cz];
      [cx, cz] = next;
    }
    return path.length >= 2 ? path : null;
  }

  isRoad(x: number, z: number): boolean {
    return this.roads.has(`${x},${z}`);
  }

  findPath(startX: number, startZ: number, endX: number, endZ: number): Array<[number, number]> | null {
    // Snap start/end to nearest road cell
    const start = this.nearestRoad(startX, startZ);
    const end = this.nearestRoad(endX, endZ);
    if (!start || !end) return null;

    const openSet: GridNode[] = [];
    const closedSet = new Set<string>();

    const startNode: GridNode = {
      x: start[0], z: start[1],
      g: 0,
      h: this.heuristic(start[0], start[1], end[0], end[1]),
      f: 0,
      parent: null,
    };
    startNode.f = startNode.h;
    openSet.push(startNode);

    while (openSet.length > 0) {
      openSet.sort((a, b) => a.f - b.f);
      const current = openSet.shift()!;
      const key = `${current.x},${current.z}`;

      if (current.x === end[0] && current.z === end[1]) {
        return this.reconstructPath(current);
      }

      closedSet.add(key);

      for (const [dx, dz] of [[0, 1], [0, -1], [1, 0], [-1, 0]]) {
        const nx = current.x + dx;
        const nz = current.z + dz;
        const nKey = `${nx},${nz}`;

        if (closedSet.has(nKey)) continue;
        if (!this.isRoad(nx, nz)) continue;

        const g = current.g + 1;
        const h = this.heuristic(nx, nz, end[0], end[1]);
        const f = g + h;

        const existing = openSet.find(n => n.x === nx && n.z === nz);
        if (existing) {
          if (g < existing.g) {
            existing.g = g;
            existing.f = f;
            existing.parent = current;
          }
        } else {
          openSet.push({ x: nx, z: nz, g, h, f, parent: current });
        }
      }
    }

    // No path found — return straight line
    return null;
  }

  nearestRoad(x: number, z: number): [number, number] | null {
    if (this.isRoad(x, z)) return [x, z];

    // Spiral search for nearest road
    for (let r = 1; r < 20; r++) {
      for (let dx = -r; dx <= r; dx++) {
        for (let dz = -r; dz <= r; dz++) {
          if (Math.abs(dx) !== r && Math.abs(dz) !== r) continue;
          if (this.isRoad(x + dx, z + dz)) return [x + dx, z + dz];
        }
      }
    }
    return null;
  }

  private heuristic(x1: number, z1: number, x2: number, z2: number): number {
    return Math.abs(x1 - x2) + Math.abs(z1 - z2);
  }

  private reconstructPath(node: GridNode): Array<[number, number]> {
    const path: Array<[number, number]> = [];
    let current: GridNode | null = node;
    while (current) {
      path.unshift([current.x, current.z]);
      current = current.parent;
    }
    return path;
  }
}
